import { pickT } from "@repo/shared";
import type { AssetClassForecastHorizon } from "@repo/shared/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./compliance-chart-form.i18n";
import { TimeRangeField } from "./time-range-field";
import type { ComponentFormProps } from "./types";

/** Every setting is opt-out: absent means 10Y with chart and table, as before QL-414. */
export type ComplianceChartConfig = {
  title?: string;
  description?: string;
  timeRange?: AssetClassForecastHorizon;
  showChart?: boolean;
  showTable?: boolean;
};

type ComplianceChartFormProps = ComponentFormProps<ComplianceChartConfig> & {
  chart: "volatility_chart" | "equity_share_chart";
  componentDescription?: string;
};

const showsNothing = (config: ComplianceChartConfig) =>
  config.showChart === false && config.showTable === false;

export function ComplianceChartForm({
  config,
  onChange,
  chart,
  componentDescription,
}: ComplianceChartFormProps) {
  const t = useT(T);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor={`${chart}-pdf-title`}>{t.title}</Label>
        <Input
          id={`${chart}-pdf-title`}
          value={config.title || ""}
          onChange={(e) => onChange({ ...config, title: e.target.value })}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${chart}-pdf-description`}>{t.description}</Label>
        <Textarea
          id={`${chart}-pdf-description`}
          value={config.description || ""}
          onChange={(e) => onChange({ ...config, description: e.target.value })}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <TimeRangeField
        id={`${chart}-pdf-time-range`}
        value={config.timeRange}
        onChange={(timeRange) => onChange({ ...config, timeRange })}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor={`${chart}-pdf-show-chart`} className="text-base">
            {t.showChart}
          </Label>
          <p className="text-sm text-muted-foreground">{t.showChartHelp}</p>
        </div>
        <Switch
          id={`${chart}-pdf-show-chart`}
          checked={config.showChart !== false}
          onCheckedChange={(checked) => onChange({ ...config, showChart: checked })}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor={`${chart}-pdf-show-table`} className="text-base">
            {t.showTable}
          </Label>
          <p className="text-sm text-muted-foreground">
            {chart === "volatility_chart" ? t.volatilityTableHelp : t.equityShareTableHelp}
          </p>
        </div>
        <Switch
          id={`${chart}-pdf-show-table`}
          checked={config.showTable !== false}
          onCheckedChange={(checked) => onChange({ ...config, showTable: checked })}
        />
      </div>

      {showsNothing(config) && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.validationError}
        </div>
      )}

      {componentDescription && (
        <div className="p-3 bg-muted/50 rounded-md">
          <p className="text-xs text-muted-foreground">{componentDescription}</p>
        </div>
      )}
    </div>
  );
}

export function validateComplianceChartConfig(
  config: ComplianceChartConfig,
  lang = "en_GB",
): string | null {
  return showsNothing(config) ? pickT(T, lang).validationErrorLong : null;
}
